import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { create } from "zustand"
import type { PreviewImagesFragment } from "@/components/collection/drops/components/PreviewImages/PreviewImages.fragment"
import {
  syncHeroMediaToParent,
  syncLogoToParent,
  syncModulesToParent,
  syncPreviewImagesToParent,
} from "@/components/studio/page-editor/utils"
import { CollectionOverviewModulesFragment } from "./fragments"
import type { HeroMediaFragment } from "./fragments/HeroMediaFragment"
import type {
  HeroMediaData,
  PreviewImageData,
  PreviewImagesData,
} from "./types"
import {
  parseCollectionHeroMedia,
  parseCollectionModules,
  parseCollectionPreviewImages,
  parseRawModules,
} from "./utils/module-parsing"

// Re-export the shared fragment with the original name for backward compatibility
export const CollectionOverviewPreviewFragment =
  CollectionOverviewModulesFragment

type ModulesData = ReturnType<typeof parseCollectionModules>

type OverviewModules = NonNullable<
  ResultOf<typeof CollectionOverviewModulesFragment>["overview"]
>["modules"]

type OverviewPreviewStore = {
  slug: string
  modules: ModulesData
  previewImages: PreviewImagesData
  heroMedia: HeroMediaData
  logoMedia: string | null
  logoToken: string | null
  hasUnsavedChanges: boolean
  setModules: (
    collectionKey: FragmentOf<typeof CollectionOverviewPreviewFragment>,
  ) => void
  syncModules: (modules: OverviewModules) => void
  moveModule: (fromIndex: number, toIndex: number) => void
  deleteModule: (moduleIndex: number) => void
  setPreviewImages: (images: PreviewImagesData) => void
  setPreviewImagesFromCollection: (
    collectionKey: FragmentOf<typeof PreviewImagesFragment>,
  ) => void
  addPreviewImage: (image: PreviewImageData) => void
  updatePreviewImage: (index: number, image: PreviewImageData) => void
  deletePreviewImage: (index: number) => void
  setHeroMedia: (heroMedia: HeroMediaData) => void
  setHeroMediaFromCollection: (
    collectionKey: FragmentOf<typeof HeroMediaFragment>,
  ) => void
  updateDesktopHeroMedia: (media: HeroMediaData["desktopHeroMedia"]) => void
  updateMobileHeroMedia: (media: HeroMediaData["mobileHeroMedia"]) => void
  updateLogoData: (logoUrl: string | null, logoToken: string | null) => void
  saveChanges: () => void
}

export const createOverviewPreviewStore = (slug: string) =>
  create<OverviewPreviewStore>((set, get) => ({
    slug,
    modules: null,
    previewImages: [],
    heroMedia: {
      desktopHeroMedia: null,
      mobileHeroMedia: null,
    },
    logoMedia: null,
    logoToken: null,
    hasUnsavedChanges: false,
    setModules: collectionKey => {
      const { hasUnsavedChanges } = get()
      if (hasUnsavedChanges) {
        return
      }

      const modules = parseCollectionModules(collectionKey)
      set({ modules })
    },
    syncModules: rawModules => {
      const modules = parseRawModules(rawModules)
      set({ modules })
    },
    moveModule: (fromIndex, toIndex) => {
      const { modules } = get()
      if (!modules || fromIndex === toIndex) {
        return
      }

      const newModules = [...modules]
      const [movedModule] = newModules.splice(fromIndex, 1)
      newModules.splice(toIndex, 0, movedModule)

      set({ modules: newModules, hasUnsavedChanges: true })

      // Sync the reordered modules back to parent
      syncModulesToParent(newModules)
    },
    deleteModule: moduleIndex => {
      const { modules } = get()
      if (!modules || moduleIndex < 0 || moduleIndex >= modules.length) {
        return
      }

      const newModules = [...modules]
      newModules.splice(moduleIndex, 1)

      set({ modules: newModules, hasUnsavedChanges: true })

      // Sync the updated modules back to parent
      syncModulesToParent(newModules)
    },
    setPreviewImages: images => {
      set({ previewImages: images, hasUnsavedChanges: true })
      // Sync the updated preview images back to parent
      syncPreviewImagesToParent(images)
    },
    setPreviewImagesFromCollection: collectionKey => {
      const { hasUnsavedChanges } = get()
      if (hasUnsavedChanges) {
        return
      }

      const previewImages = parseCollectionPreviewImages(collectionKey)
      set({ previewImages })
    },
    addPreviewImage: image => {
      const { previewImages } = get()
      if (previewImages.length >= 4) {
        return // Max 4 images
      }

      const newImages = [...previewImages, image]
      set({ previewImages: newImages, hasUnsavedChanges: true })
      // Sync the updated preview images back to parent
      syncPreviewImagesToParent(newImages)
    },
    updatePreviewImage: (index, image) => {
      const { previewImages } = get()
      if (index < 0 || index >= previewImages.length) {
        return
      }

      const newImages = [...previewImages]
      newImages[index] = image
      set({ previewImages: newImages, hasUnsavedChanges: true })
      // Sync the updated preview images back to parent
      syncPreviewImagesToParent(newImages)
    },
    deletePreviewImage: index => {
      const { previewImages } = get()
      if (index < 0 || index >= previewImages.length) {
        return
      }

      const newImages = [...previewImages]
      newImages.splice(index, 1)
      set({ previewImages: newImages, hasUnsavedChanges: true })
      // Sync the updated preview images back to parent
      syncPreviewImagesToParent(newImages)
    },
    setHeroMedia: heroMedia => {
      set({ heroMedia, hasUnsavedChanges: true })
      // Sync the updated hero media back to parent
      syncHeroMediaToParent(heroMedia)
    },
    setHeroMediaFromCollection: collectionKey => {
      const { hasUnsavedChanges } = get()
      if (hasUnsavedChanges) {
        return
      }

      const heroMedia = parseCollectionHeroMedia(collectionKey)
      set({ heroMedia })
    },
    updateDesktopHeroMedia: media => {
      const { heroMedia } = get()
      const newHeroMedia = { ...heroMedia, desktopHeroMedia: media }
      set({ heroMedia: newHeroMedia, hasUnsavedChanges: true })
      // Sync the updated hero media back to parent
      syncHeroMediaToParent(newHeroMedia)
    },
    updateMobileHeroMedia: media => {
      const { heroMedia } = get()
      const newHeroMedia = { ...heroMedia, mobileHeroMedia: media }
      set({ heroMedia: newHeroMedia, hasUnsavedChanges: true })
      // Sync the updated hero media back to parent
      syncHeroMediaToParent(newHeroMedia)
    },
    updateLogoData: (logoUrl: string | null, logoToken: string | null) => {
      set({ logoMedia: logoUrl, logoToken, hasUnsavedChanges: true })
      syncLogoToParent({ logoMedia: logoUrl, logoToken })
    },
    saveChanges: () => {
      set({ hasUnsavedChanges: false })
    },
  }))
